import { useContext, useEffect, useRef } from 'react'

import ModalContext from './ModalContext'
import './Modal.scss'
import useOutsideOnClick from 'hooks/useOutsideOnClick'
import classNames from 'classnames'
import { useTransition, animated } from 'react-spring'
import useBreakpoint from 'hooks/useBreakPoint'

const BaseModal: React.FC = ({ children }) => {
  const {
    show,
    onHide,
    baseModalClass,
    modalContainerClass,
    modalContentClass,
  } = useContext(ModalContext)
  const { isSmallSize } = useBreakpoint()

  const modalTransition = isSmallSize
    ? useTransition(show, {
        from: { transform: 'translateY(100%)' },
        enter: { transform: 'translateY(0%)' },
        leave: { transform: 'translateY(100%)' },
      })
    : useTransition(show, {
        from: { opacity: '0' },
        enter: { opacity: '1' },
        leave: { opacity: '0' },
        config: { duration: 100 },
      })
  const modalContentRef = useRef<HTMLDivElement>(null)

  useOutsideOnClick({
    containerRef: modalContentRef,
    callback: onHide,
  })

  useEffect(() => {
    if (show && document.body.style.overflow !== 'hidden') {
      document.body.style.overflow = 'hidden'
    } else if (!show && document.body.style.overflow !== 'unset') {
      document.body.style.overflow = 'unset'
    }

    return () => {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = 'unset'
      }
    }
  }, [show])

  return modalTransition(
    (style, item) =>
      item && (
        <animated.div
          style={style}
          role="dialog"
          aria-modal
          className={`BaseModal ${baseModalClass}`}
        >
          <div className={`BaseModal__container ${modalContainerClass}`}>
            <div
              className={classNames(`BaseModal__content ${modalContentClass}`)}
              ref={modalContentRef}
            >
              {children}
            </div>
          </div>
        </animated.div>
      )
  )
}

export default BaseModal
